// The XERO PURCHASE ORDERS section on a supply and fit company's page (Session RC2; spec section 8: the page shows purchase
// orders and Xero bills in place of the rate card). ADMIN ONLY: the page renders it only for an admin, because it carries
// what ELE orders from and pays the company, and the server refuses the read to anyone else. Never on the Subcontractor
// Portal. These are XERO purchase orders, not the platform's own van parts orders.
//
// Read from Xero on the company's recorded payable contact (the same contact the invoice status section below uses), the
// last 24 months: number, date, delivery date, reference, total and status. A read that cannot be made says why, in one
// sentence, and never shows an empty list in its place. "Read Xero again" skips the five-minute cache. Read only.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { XeroPurchaseOrdersView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate, formatDateTime, gbpExact } from "../../lib/format";
import { statusEntry } from "../../lib/statusRegistry";


function money(total: number | null, currency: string | null): string {
  if (total == null) return "Not recorded";
  return currency && currency !== "GBP" ? `${currency} ${total.toFixed(2)}` : gbpExact(total);
}

export function SubcontractorXeroPurchaseOrdersSection({ subId }: { subId: string }) {
  const [view, setView] = useState<XeroPurchaseOrdersView | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    (fresh: boolean) => {
      let live = true;
      if (fresh) setBusy(true);
      setError(null);
      api.subcontractors
        .xeroPurchaseOrders(subId, fresh)
        .then((d) => {
          if (!live) return;
          setView(d.xeroPurchaseOrders);
          setState("ready");
        })
        .catch((e) => {
          if (!live) return;
          setError(friendlyApiError(e, "Could not load the Xero purchase orders. Please try again in a moment."));
          setState((s) => (s === "loading" ? "error" : s));
        })
        .finally(() => live && setBusy(false));
      return () => {
        live = false;
      };
    },
    [subId],
  );

  useEffect(() => load(false), [load]);

  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Xero purchase orders">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg uppercase tracking-wide text-ele-blue">Xero purchase orders</h2>
        {state === "ready" ? (
          <Button size="sm" variant="secondary" loading={busy} onClick={() => load(true)}>
            Read Xero again
          </Button>
        ) : null}
      </div>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="alert" className="text-sm text-ele-strong">{error ?? "Could not load the Xero purchase orders."}</p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ele-body">
            The purchase orders raised to this company in Xero, against their own quotes, from the last 24 months. Not the van parts orders on the Purchase orders page.
          </p>
          {error ? (
            <p role="alert" className="text-sm text-ele-strong">
              {error}
            </p>
          ) : null}
          {!view.available ? (
            <p className="rounded-ele border border-ele-purple-accent bg-ele-base px-3 py-2 text-sm text-ele-strong">{view.message}.</p>
          ) : view.orders.length === 0 ? (
            // RC2 review follow-up: a read cut short at 1,000 orders cannot say there are none; the note below says what was read.
            view.truncated ? (
              <p className="text-sm text-ele-body">None found among the purchase orders read from Xero.</p>
            ) : (
              <p className="text-sm text-ele-body">No Xero purchase orders to this company in the last 24 months.</p>
            )
          ) : (
            <table className="ele-list-table w-full text-left text-sm">
              <thead className="font-menu text-xs uppercase tracking-wide text-ele-blue">
                <tr>
                  <th className="py-1 pr-2">Number</th>
                  <th className="py-1 pr-2">Date</th>
                  <th className="py-1 pr-2">Delivery</th>
                  <th className="py-1 pr-2">Reference</th>
                  <th className="py-1 pr-2">Total</th>
                  <th className="py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {view.orders.map((o, i) => (
                  <tr key={`${o.number ?? "none"}-${i}`} className="border-t border-ele-purple-accent align-top">
                    <td className="py-1.5 pr-2 text-ele-strong">{o.number ?? "No number"}</td>
                    <td className="py-1.5 pr-2 text-ele-strong">{o.date ? formatDate(o.date) : "Not recorded"}</td>
                    <td className="py-1.5 pr-2 text-ele-strong">{o.deliveryDate ? formatDate(o.deliveryDate) : "Not recorded"}</td>
                    <td className="py-1.5 pr-2 text-ele-strong">{o.reference ?? "None"}</td>
                    <td className="py-1.5 pr-2 text-ele-strong">{money(o.total, o.currency)}</td>
                    <td className="py-1.5">
                      {/* The words and tones live in the one status registry (domain xeroPurchaseOrder), never a local map. */}
                      <StatusPill word={statusEntry("xeroPurchaseOrder", o.status).label} tone={statusEntry("xeroPurchaseOrder", o.status).tone} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {view.available && view.truncated ? <p className="text-xs text-ele-body">Only the first 1,000 purchase orders in Xero were read, so this list may be missing some.</p> : null}
          {view.available ? <p className="text-xs text-ele-body">Read from Xero at {formatDateTime(view.readAt)}.</p> : null}
        </div>
      )}
    </section>
  );
}
